import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";
import Chip from "@material-ui/core/Chip";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	btnWrapper: {
		position: "relative",
	},
	buttonProgress: {
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	chips: {
		display: "flex",
		flexWrap: "wrap",
		gap: 4,
	},
}));

const RequestTypeSchema = Yup.object().shape({
	name: Yup.string()
		.trim()
		.min(3, "Muito curto!")
		.max(255, "Muito longo!")
		.required("Obrigatório"),
	slaHours: Yup.number()
		.typeError("Deve ser um número")
		.integer("Deve ser um número inteiro")
		.min(1, "Deve ser maior que zero")
		.required("Obrigatório"),
});

// Serve criacao e edicao - editingType null => criacao. Sem campo "active"
// aqui de proposito: o toggle na tabela do catalogo e o unico lugar que
// muda esse estado, para nao existirem dois caminhos de UI para a mesma
// coisa.
const RequestTypeModal = ({ open, onClose, editingType, onSaved }) => {
	const classes = useStyles();

	// Conexoes da empresa, para o multi-select de escopo. Vem do proprio
	// Whaticket (GET /whatsapp), ja filtrado por empresa pelo isAuth - o
	// modulo nao guarda copia dessa lista.
	const [connections, setConnections] = useState([]);
	const [whatsappIds, setWhatsappIds] = useState([]);
	// O multi-select vive fora do Formik (estado proprio), entao a validacao
	// de obrigatoriedade tambem e manual. So marca erro depois da primeira
	// tentativa de salvar - avisar antes de o usuario ter chance de escolher
	// seria ruido.
	const [tentouSalvar, setTentouSalvar] = useState(false);
	const semConexao = whatsappIds.length === 0;

	const initialState = {
		name: editingType?.name || "",
		slaHours: editingType?.sla_hours || "",
	};

	useEffect(() => {
		if (!open) return;

		// Pre-marca a partir do que o backend devolveu para este tipo.
		setWhatsappIds(editingType?.whatsapp_ids || []);
		setTentouSalvar(false);

		const fetchConnections = async () => {
			try {
				const { data } = await api.get("/whatsapp");
				setConnections(Array.isArray(data) ? data : []);
			} catch (err) {
				toastError(err);
			}
		};
		fetchConnections();
	}, [open, editingType]);

	const handleClose = () => {
		onClose();
	};

	const handleSave = async (values, { resetForm }) => {
		// Trava do frontend. O backend recusa igual (Create/Update devolvem
		// 400) - esta aqui e so para o erro aparecer no campo, sem ida ao
		// servidor.
		setTentouSalvar(true);
		if (whatsappIds.length === 0) return;

		try {
			if (editingType) {
				await api.patch(`/solicitacoes/tipos/${editingType.id}`, {
					name: values.name,
					slaHours: values.slaHours,
					// Sempre enviado pelo modal - e aqui que o admin decide o
					// escopo. Quem NAO manda esta chave e o toggle Ativo/Inativo
					// da tabela: por isso "ausente" significa "nao mexer nos
					// vinculos" no backend, enquanto lista vazia e recusada.
					whatsappIds,
				});
				toast.success(i18n.t("requestTypeModal.toasts.updateSuccess"));
			} else {
				await api.post("/solicitacoes/tipos", {
					name: values.name,
					slaHours: values.slaHours,
					whatsappIds,
				});
				toast.success(i18n.t("requestTypeModal.toasts.createSuccess"));
			}
			resetForm();
			onSaved();
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Dialog
			id="request-type-modal"
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			scroll="paper"
		>
			<DialogTitle>
				{editingType
					? i18n.t("requestTypeModal.titleEdit")
					: i18n.t("requestTypeModal.titleCreate")}
			</DialogTitle>
			<Formik
				initialValues={initialState}
				enableReinitialize
				validationSchema={RequestTypeSchema}
				onSubmit={handleSave}
			>
				{({ touched, errors, isSubmitting }) => (
					// noValidate: o "required" do FormControl faz o MUI marcar o
					// input interno do Select como obrigatorio, e a validacao
					// nativa do HTML5 barra o submit ANTES do Formik - aparecendo
					// o balao do navegador ("Preencha este campo") no idioma dele,
					// e deixando a nossa mensagem sem nunca rodar. Desligando a
					// nativa, quem valida e o Formik/nossa checagem, com a
					// mensagem em pt e no estilo do resto do formulario.
					<Form noValidate>
						<DialogContent dividers>
							<Grid container spacing={1}>
								<Grid item xs={12}>
									<Field
										as={TextField}
										label={i18n.t("requestTypeModal.form.name")}
										name="name"
										autoFocus
										fullWidth
										variant="outlined"
										margin="dense"
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
									/>
								</Grid>
								<Grid item xs={12}>
									<Field
										as={TextField}
										label={i18n.t("requestTypeModal.form.slaHours")}
										name="slaHours"
										type="number"
										fullWidth
										variant="outlined"
										margin="dense"
										error={touched.slaHours && Boolean(errors.slaHours)}
										helperText={touched.slaHours && errors.slaHours}
									/>
								</Grid>
								<Grid item xs={12}>
									{/* Escopo por conexao - OBRIGATORIO. Uma demanda sem conexao
									    voltaria a aparecer em todas as linhas, que e exatamente o
									    que esta regra existe para impedir. O backend recusa igual
									    (400); esta trava e so para o erro aparecer no campo, sem
									    ida ao servidor. */}
									<FormControl
										variant="outlined"
										margin="dense"
										fullWidth
										required
										error={tentouSalvar && semConexao}
									>
										<InputLabel id="request-type-whatsapps-label">
											{i18n.t("requestTypeModal.form.connections")}
										</InputLabel>
										<Select
											labelId="request-type-whatsapps-label"
											id="request-type-whatsapps"
											multiple
											value={whatsappIds}
											onChange={e => setWhatsappIds(e.target.value)}
											label={i18n.t("requestTypeModal.form.connections")}
											renderValue={selecionados => (
												<div className={classes.chips}>
													{selecionados.map(id => {
														const conexao = connections.find(c => c.id === id);
														return (
															<Chip
																key={id}
																size="small"
																label={conexao ? conexao.name : `#${id}`}
															/>
														);
													})}
												</div>
											)}
										>
											{connections.map(conexao => (
												<MenuItem key={conexao.id} value={conexao.id}>
													<Checkbox
														color="primary"
														checked={whatsappIds.indexOf(conexao.id) > -1}
													/>
													<ListItemText primary={conexao.name} />
												</MenuItem>
											))}
										</Select>
										<FormHelperText>
											{tentouSalvar && semConexao
												? i18n.t("requestTypeModal.form.connectionsRequired")
												: i18n.t("requestTypeModal.form.connectionsHint")}
										</FormHelperText>
									</FormControl>
								</Grid>
							</Grid>
						</DialogContent>
						<DialogActions>
							<Button
								onClick={handleClose}
								color="secondary"
								disabled={isSubmitting}
								variant="outlined"
							>
								{i18n.t("requestTypeModal.buttons.cancel")}
							</Button>
							<Button
								type="submit"
								color="primary"
								disabled={isSubmitting}
								variant="contained"
								className={classes.btnWrapper}
							>
								{editingType
									? i18n.t("requestTypeModal.buttons.save")
									: i18n.t("requestTypeModal.buttons.create")}
								{isSubmitting && (
									<CircularProgress
										size={24}
										className={classes.buttonProgress}
									/>
								)}
							</Button>
						</DialogActions>
					</Form>
				)}
			</Formik>
		</Dialog>
	);
};

export default RequestTypeModal;
